import React, { useContext, useState } from 'react';
import { Link, generatePath, useHistory, matchPath } from 'react-router-dom';
import Bluebird from 'bluebird';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  AppBar,
  Menu,
  Fade,
  MenuItem,
  Toolbar,
  Avatar,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Divider,
  ListItemIcon,
  Badge,
  Link as MuiLink,
  useTheme,
  useMediaQuery,
  Box,
  styled,
} from '@mui/material';
import {
  PriorityHigh as PriorityHighIcon,
  Menu as MenuIcon,
  Help as HelpIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { Steps } from 'intro.js-react';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import frusLogo from '../assets/images/new-frus-logo-oval.png';
import NotificationsContainer from './NotificationsContainer';
import * as authService from '../services/auth';
import routes from '../routes';
import MainDrawer from './MainDrawer';
import * as uiActions from '../actions/ui';
import * as uiPersistedActions from '../actions/uiPersisted';
import { siteDirectorTour, tourOfTours, mobileDashboard, ReceiverMobileSchedule } from '../helpers/tours';
import { Colors } from '../assets/theme/Colors';
import { Roles } from '../models/roles';
import {
  getCurrentlyLoggedInOrImpersonatingUser,
  getCurrentlyLoggedInOrImpersonatingUserSitesByRole,
  hasRoleInCurrentlySelectedSite,
} from '../services/auth';
import { getUserFullName, getUserImage } from '../helpers/user';
import { openDeleteAccountDialog } from '../helpers/deleteAccount';
import * as authActions from '../actions/auth';
import FABContext from '../context/FABContext/FABContext';
import {
  FF_EVENTS,
  FF_LEARNING_CENTER,
  FF_MARKETING_HUB,
  FF_RECEIVING_AGENCY_REPORT,
  FF_TAX_DOCUMENTS,
} from './FeatureFlagWrapper';
import { setFABActions } from '../helpers/fab';
import useActiveSite from '../hooks/useActiveSite';
import useHasActiveFeatureFlag from '../hooks/useFeatureFlags';
import { setShowTourOfTours } from '../actions/uiPersisted';
import useNotificationService from '../hooks/useNotificationService';
import { createCrudAction } from '../api/crudActions';

const drawerWidth = 220;

const ButtonAppBar = ({ onLogoutClick, location, hasCustomBranding, customBranding = {} }) => {
  const appBarColor =
    hasCustomBranding && customBranding?.primary_color ? customBranding.primary_color : Colors.appBar.main;

  const [menuAnchorEl, setMenuAnchorEl] = useState();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const site = useActiveSite();
  const context = useContext(FABContext);
  const history = useHistory();
  const hasDisableRequestToDonate = useSelector((state) => state.ui.uiDisabled);
  const isDrawerOpen = useSelector((state) => state.ui.persisted.isDrawerOpen);
  const idTokenPayload = useSelector((state) => state.app.idTokenPayload);
  const loggedUser = useSelector((state) =>
    state.app.loggedInUser
      ? {
          ...state.app.loggedInUser,
          picture: getUserImage(state.app.loggedInUser, idTokenPayload),
        }
      : state.app.loggedInUser
  );
  const impersonatedUser = useSelector((state) => state.app.impersonating);
  const showTourOfTours = useSelector((state) => state.ui.persisted.showTourOfTours);
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.only('xs'));
  const { addErrorNotification } = useNotificationService();
  const hasEventsFF = useHasActiveFeatureFlag(FF_EVENTS);
  const hasReceivingAgencyReportFF = useHasActiveFeatureFlag(FF_RECEIVING_AGENCY_REPORT);
  const hasMarketingHubFF = useHasActiveFeatureFlag(FF_MARKETING_HUB);
  const hasActiveLearningCenterFF = useHasActiveFeatureFlag(FF_LEARNING_CENTER);
  const hasTaxDocumentsFF = useHasActiveFeatureFlag(FF_TAX_DOCUMENTS);
  const isWelcomePage = matchPath(location.pathname, { path: routes.welcomePage, exact: true });
  const isLicensee = site?.type === 'licensee';
  const DisableLinks = isWelcomePage && Capacitor.isNativePlatform();
  const showRescuerMobileTour =
    isMobileView &&
    authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([Roles.Rescuer]) &&
    (
      // Mobile Dashboard
      matchPath(location.pathname, {
        path: routes.mobileDashboard,
        exact: true,
      }) || 
      // Rescuer Dashboard
      matchPath(location.pathname, {
        path: routes.dashboardRescuer,
        exact: true,
      })
    );

  const showSiteDirectorMobileTour =
    isMobileView &&
    authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
      Roles.SiteDirector,
      Roles.SiteCoordinator,
    ]) && (
      // SD Dashboard
      matchPath(location.pathname, {
        path: routes.dashboardSC,
        exact: true,
      }) ||
      // Mobile Dashboard
      matchPath(location.pathname, {
        path: routes.mobileDashboard,
        exact: true,
      })
    );

  const showReceiverMobileTour =
    isMobileView &&
    authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
      Roles.ReceiverLocationAdmin,
      Roles.ReceiverLocationStaff,
      Roles.ReceiverAdmin,
      Roles.ReceiverStaff,
    ]) && (
      // RA Dashboard
      matchPath(location.pathname, {
        path: routes.dashboardReceiver,
        exact: true,
      }) ||
      // Mobile Dashboard
      matchPath(location.pathname, {
        path: routes.mobileDashboard,
        exact: true,
      }) ||
      // RA Mobile Shedule
      matchPath(location.pathname, {
        path: routes.mobileDashboardReceiverSchedule,
        exact: true,
      })
    );

  const getCustomAppBarTextColor = () => {
    if (customBranding?.toolbar_text_color && hasCustomBranding) {
      return customBranding.toolbar_text_color;
    }
    return theme.palette.getContrastText(appBarColor);
  };

  const newReleasesIconSx = {
    color: hasCustomBranding ? getCustomAppBarTextColor(customBranding, theme) : 'yellow',
  };
  const appBarButtonSx = {
    mr: 0,
    ml: 0,
  };

  const openDrawer = () => dispatch(uiPersistedActions.openDrawer());
  const closeDrawer = () => dispatch(uiPersistedActions.closeDrawer());
  const setTourStepper = (enabled, payload) => dispatch(uiActions.setTourStepper(enabled, payload));
  const endImpersonation = (loggedInUser) => dispatch(authActions.changeUser(loggedInUser.id));

  const closeRightSideMenu = () => {
    setMenuAnchorEl(null);
  };

  const openRightSideMenu = event => {
    if (DisableLinks) {
      return null;
    }
    return setMenuAnchorEl(event.currentTarget);
  };

  const inviteFriend = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // native invite
        await Share.share({
          title: 'Join Food Rescue US',
          text: 'I volunteer with Food Rescue US to help reduce food waste and feed our community. You should join me!',
          url: 'https://app.foodrescue.us/signup',
          dialogTitle: 'Invite a friend',
        });
      } else {
        // web invite
        await navigator.share({
          title: 'Food Rescue US',
          text: 'Join Food Rescue!',
          url: 'https://app.foodrescue.us/signup',
        });
      }

      closeRightSideMenu();

      // log rescuer invited event
      if (site) {
        await createCrudAction({
          site_id: site.id,
          action_type: 'RESCUER_INVITED',
        });
      }
    } catch (err) {
      closeRightSideMenu();
    }
  };

  const handleTourClick = () => {
    // RA Mobile Schedule
    if (
      showReceiverMobileTour &&
      matchPath(location.pathname, {
        path: routes.mobileDashboardReceiverSchedule,
        exact: true,
      })
    ) {
      return setTourStepper(true, ReceiverMobileSchedule);
    }

    // Mobile Dashboard
    return setTourStepper(true, mobileDashboard);
  };

  // for now there are 3 states:
  // 1 - not logged in: (idTokenPayload null)
  // 2 - logged in, not impersonating: (idTokenPayload !== null, impersonating null)
  // 3 - impersonating: (idTokenPayload !== null, impersonating !== null )
  // we need the name, avatar, and roles for now
  const user = impersonatedUser || loggedUser;
  const isImpersonating = Boolean(impersonatedUser);

  const isCompleteRegistrationProcessPending = authService.isCompleteRegistrationProcessPending();
  const shouldHideMainDrawer = authService.shouldHideMainDrawer(user, isMobileView);
  const termsOfServiceLink = hasRoleInCurrentlySelectedSite(user, Roles.DonorLocationAdmin)
    ? generatePath(routes.termsOfService, { type: 'food-donor' })
    : generatePath(routes.termsOfService, { type: 'rescuer' });

  const Offset = styled('div')(({ theme }) => ({
    ...theme.mixins.toolbar,
    minHeight: `calc(${theme.mixins.toolbar.minHeight}px + var(--ion-safe-area-top))`,
  }));

  // Do not display AppBar when in ID mode or on the email-changed landing page
  if (location.pathname === routes.idMode || location.pathname === routes.emailChanged) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={[
          {
            pt:
              Capacitor.getPlatform() === 'android'
                ? 'var(--ion-safe-area-top)'
                : 'calc(env(safe-area-inset-top, 64px) + 3px)',
            pb: '3px',
            bgcolor: appBarColor || Colors.appBar.main,
            zIndex: theme.zIndex.drawer + 1,
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          },
          isDrawerOpen && {
            ml: `${drawerWidth}px`,
            width: {
              sm: `calc(100% - ${drawerWidth}px)`,
            },
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        ]}
      >
        {/* Do not show tour of tours on native -> replaced with native welcome tour */}
        {showRescuerMobileTour && !Capacitor.isNativePlatform() && (
          <Steps
            enabled={showTourOfTours || showTourOfTours === undefined}
            options={{
              nextToDone: true,
              hidePrev: true,
            }}
            onExit={() => dispatch(setShowTourOfTours(false))}
            initialStep={0}
            steps={tourOfTours}
          />
        )}
        <Toolbar
          disableGutters={!isDrawerOpen}
          variant="dense"
          sx={{
            paddingRight: { xs: '8px', sm: '24px' }, // keep right padding when drawer closed
            paddingLeft: { xs: '8px', sm: '5px' },
          }}
        >
          {user && !shouldHideMainDrawer && (
            <IconButton
              sx={[
                {
                  color: getCustomAppBarTextColor(),
                },
                isDrawerOpen && {
                  display: 'none',
                },
              ]}
              color="inherit"
              aria-label="Menu"
              onClick={openDrawer}
              size="large"
            >
              <MenuIcon data-introid="hamburger-menu" />
            </IconButton>
          )}

          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
            }}
          >
            <Typography
              variant="h6"
              color="inherit"
              sx={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                fontSize: '16px !important',
              }}
              component={Link}
              to={routes.index}
              onClick={(e) => DisableLinks && e.preventDefault()}
              data-testid="appbar-logo"
              data-introid="page-title"
            >
              {isMobileView ? (
                <img
                  alt="frus logo"
                  style={{ width: '30px', height: '30px', marginRight: '3px', marginLeft: '5px' }}
                  src={hasCustomBranding && customBranding?.logo_url ? customBranding?.logo_url : frusLogo}
                />
              ) : (
                <>
                  <img
                    alt="frus logo"
                    style={{ width: '30px', height: '30px', marginRight: '3px', marginLeft: '5px' }}
                    src={frusLogo}
                  />

                  {hasCustomBranding && customBranding?.logo_url && (
                    <>
                      <Box
                        component="span"
                        sx={{
                          color: getCustomAppBarTextColor(),
                          marginLeft: '6px',
                          marginRight: '6px',
                          '@media (max-width: 649px)': {
                            marginLeft: 0,
                            marginRight: 0,
                          },
                        }}
                      >
                        |
                      </Box>
                      <img
                        alt="brand logo"
                        style={{
                          height: '30px',
                          width: '30px',
                          objectFit: 'cover',
                          marginRight: '3px',
                          marginLeft: '3px',
                        }}
                        src={customBranding?.logo_url}
                      />
                    </>
                  )}
                </>
              )}

              {isCompleteRegistrationProcessPending ? (
                <div style={{ marginLeft: 12 }}>Food Rescue US</div>
              ) : (
                <>
                  {site?.type !== 'licensee' && (
                    <Box
                      sx={{
                        marginLeft: '12px',
                        display: 'none',
                        '@media (min-width: 650px)': {
                          display: 'block',
                        },
                        color: getCustomAppBarTextColor(),
                      }}
                    >
                      Food Rescue US
                    </Box>
                  )}
                  {(isMobileView &&
                      getCurrentlyLoggedInOrImpersonatingUserSitesByRole(Roles.Rescuer).length > 1 &&
                      getCurrentlyLoggedInOrImpersonatingUser().role_assignments.filter(
                        (r) => r.role_name !== Roles.Rescuer
                      ).length === 0) === false &&
                    user && (
                      <Box
                        component="span"
                        sx={
                          site?.type !== 'licensee'
                            ? {
                                color: getCustomAppBarTextColor(),
                                '@media (max-width: 649px)': {
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 'calc(100vw - 240px)',
                                },
                              }
                            : {
                                color: getCustomAppBarTextColor(),
                                '@media (max-width: 649px)': {
                                  marginLeft: 0,
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 'calc(100vw - 230px)',
                                },
                              }
                        }
                        data-testid="active-site-span"
                      >
                        {site?.type !== 'licensee' && (
                          <Box
                            component="span"
                            sx={{
                              '@media (max-width: 649px)': {
                                display: 'none',
                              },
                            }}
                          >
                            &nbsp;-&nbsp;
                          </Box>
                        )}

                        {site && site.name ? site.name : 'Supporter Community'}
                      </Box>
                    )}
                </>
              )}
            </Typography>
          </Box>

          {user && (
            <Box display="flex" data-introid="user-toolbar">
              {!DisableLinks && (
                <NotificationsContainer hasCustomBranding={hasCustomBranding} customBranding={customBranding} />
              )}

              {!isMobileView && authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
                  Roles.SiteDirector,
                  Roles.SiteCoordinator,
                ]) && (
                  <Tooltip title="Tour This Page">
                    <IconButton
                      color="inherit"
                      aria-label="help"
                      onClick={() => {
                        closeRightSideMenu();
                        if (isDrawerOpen) {
                          closeDrawer();
                          return setTimeout(() => {
                            setTourStepper(true, siteDirectorTour);
                          }, 500); // Allow closing drawer animation to finish before the tour
                        }
                        return setTourStepper(true, siteDirectorTour);
                      }}
                      size="large"
                    >
                      <HelpIcon sx={newReleasesIconSx} />
                    </IconButton>
                  </Tooltip>
                )}

              {(showRescuerMobileTour || showSiteDirectorMobileTour || showReceiverMobileTour) && (
                <Tooltip title="Tour This Page" data-introid="tours">
                  <IconButton color="inherit" aria-label="help" onClick={handleTourClick} size="large">
                    <HelpIcon sx={newReleasesIconSx} />
                  </IconButton>
                </Tooltip>
              )}

              <IconButton
                onClick={openRightSideMenu}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '5px',
                  marginLeft: '5px',
                  borderRadius: '5px',
                }}
                aria-controls="avatar-menu"
                aria-haspopup="true"
                data-testid="profile-menu-toggle"
                data-introid="profile-menu-toggle"
                disableRipple
                size="large"
              >
                <Badge
                  color="secondary"
                  invisible={!isImpersonating}
                  badgeContent="!"
                  sx={{
                    '& .MuiBadge-anchorOriginTopRightRectangular': {
                      top: 5,
                      right: 5,
                    },
                  }}
                >
                  <Avatar
                    aria-label="User avatar"
                    style={
                      hasCustomBranding && customBranding?.primary_color
                        ? {
                          backgroundColor: customBranding.primary_color,
                          backgroundImage: 'linear-gradient(rgb(0 0 0/20%) 0 0)',
                        }
                        : { backgroundColor: '#c7392f' }
                    }
                    src={user?.picture}
                  />
                </Badge>

                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography
                    sx={{ fontSize: '16px !important', marginLeft: '6px', color: getCustomAppBarTextColor() }}
                    variant="h6"
                    color="inherit"
                  >
                    {getUserFullName(user)}
                  </Typography>
                </Box>

                <ArrowDropDownIcon sx={{ color: getCustomAppBarTextColor() }} />
              </IconButton>
              <Menu
                id="avatar-menu"
                keepMounted
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={closeRightSideMenu}
                TransitionComponent={Fade}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                <MenuItem onClick={closeRightSideMenu} component={Link} to={generatePath(routes.profile)} key="profile">
                  {formatMessage({ id: 'app-bar.menu.profile' })}
                </MenuItem>
                <MenuItem
                  onClick={closeRightSideMenu}
                  component={Link}
                  to={routes.notificationsPreferences}
                  key="preferences"
                >
                  {formatMessage({ id: 'app-bar.menu.preferences' })}
                </MenuItem>
                <MenuItem
                  onClick={closeRightSideMenu}
                  component={MuiLink}
                  target="_blank"
                  href="https://support.foodrescue.us/hc/en-us"
                  key="user-manual"
                  data-introid="user-manual"
                >
                  {formatMessage({ id: 'app-bar.menu.user-manual' })}
                </MenuItem>
                {isLicensee && (
                  <MenuItem
                    onClick={closeRightSideMenu}
                    component={MuiLink}
                    to={routes.notificationsPreferences}
                    key="about-the-tech"
                    href="https://foodrescue.us/our-app/"
                    target="_blank"
                  >
                    {formatMessage({ id: 'app-bar.menu.about-the-tech' })}
                  </MenuItem>
                )}

                {site && !isImpersonating && (
                  <MenuItem onClick={inviteFriend} component={MuiLink} key="invite-a-friend" href="#">
                    {formatMessage({ id: 'app-bar.menu.invite-a-friend' })}
                  </MenuItem>
                )}

                {isMobileView && authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
                  Roles.SiteDirector,
                  Roles.SiteCoordinator,
                ]) && (
                  <MenuItem
                    onClick={closeRightSideMenu}
                    component={Link}
                    to={generatePath(routes.contactList, { tab: 'receivers' })}
                    key="contact-list"
                  >
                    {formatMessage({ id: 'app-bar.menu.contact-list' })}
                  </MenuItem>
                )}
                {isMobileView &&
                  authService.hasAnyRoleInCurrentlySelectedSite(loggedUser, [
                    Roles.Admin,
                    Roles.NationalSiteDirector,
                  ]) && (
                    <MenuItem
                      onClick={closeRightSideMenu}
                      component={Link}
                      to={routes.switchUser}
                      key="impersonate"
                    >
                      {formatMessage({ id: 'app-bar.menu.switch-user' })}
                    </MenuItem>
                  )}
                {isImpersonating && (
                  <MenuItem
                    key="end-impersonate"
                    fontSize="small"
                    onClick={() => Bluebird
                      .try(() => endImpersonation(loggedUser))
                      .then(() => setFABActions(loggedUser, history, context))
                    }
                  >
                    <ListItemIcon style={{ minWidth: 25 }}>
                      <PriorityHighIcon fontSize="small" />
                    </ListItemIcon>
                    {formatMessage({ id: 'app-bar.menu.end-impersonation' })}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={closeRightSideMenu}
                  component={Link}
                  to={routes.contactUs}
                  key="contact-us"
                  data-testid="contactus-link"
                >
                  {formatMessage({ id: 'app-bar.menu.contact-us' })}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    closeRightSideMenu();
                    onLogoutClick();
                  }}
                  key="logout"
                  data-testid="logout-link"
                >
                  {formatMessage({ id: 'app-bar.menu.logout' })}
                </MenuItem>

                {Capacitor.getPlatform() === 'ios' && !isImpersonating && (
                  <MenuItem
                    onClick={() => {
                      closeRightSideMenu();
                      openDeleteAccountDialog();
                    }}
                    key="delete-account"
                    data-testid="delete-account-link"
                    sx={{ color: Colors.errorText || '#D32F2F' }}
                  >
                    Delete Account
                  </MenuItem>
                )}

                <Divider light />

                <MenuItem
                  onClick={closeRightSideMenu}
                  component={Link}
                  to={termsOfServiceLink}
                  key="tos"
                  data-testid="terms-of-service"
                >
                  {formatMessage({ id: 'app-bar.menu.terms-of-service' })}
                </MenuItem>

                <Divider light />

                <MenuItem key="version" fontSize="small" style={{ minHeight: 42, fontSize: '.9rem' }}>
                  <ListItemIcon>
                    <img
                      alt="frus logo"
                      style={{ width: '30px', height: '30px', marginRight: '3px', marginLeft: '5px' }}
                      src={frusLogo}
                    />
                  </ListItemIcon>

                  {process.env.REACT_APP_VERSION}
                  {process.env.REACT_APP_BUILD_NUMBER ? ` (${process.env.REACT_APP_BUILD_NUMBER})` : ''}
                </MenuItem>
              </Menu>
            </Box>
          )}

          {!user && (
            <>
              <Button
                component={Link}
                sx={appBarButtonSx}
                color="inherit"
                to={routes.contactUsNational}
                data-testid="contact_us-button"
              >
                Contact Us
              </Button>

              {!(location.pathname === routes.login || location.pathname === routes.index) && (
                <Button
                  component={Link}
                  to={routes.login}
                  sx={{ mr: '5px' }}
                  color="inherit"
                  data-testid="login-button"
                >
                  Login
                </Button>
              )}

              {!(
                matchPath(location.pathname, {
                  path: routes.signup,
                }) ||
                matchPath(location.pathname, {
                  path: routes.completeRegistrationNew,
                }) ||
                matchPath(location.pathname, {
                  path: routes.completeRescuerRegistration,
                })
              ) && (
                <Button
                  component={Link}
                  to={routes.signup}
                  sx={appBarButtonSx}
                  color="inherit"
                  data-testid="register-button"
                  variant="outlined"
                >
                  Sign Up
                </Button>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>

      <Offset />

      <nav>
        {user && !isCompleteRegistrationProcessPending && !shouldHideMainDrawer && (
          <MainDrawer
            featureFlagsList={{
              events: hasEventsFF,
              receivingAgencyReport: hasReceivingAgencyReportFF,
              marketingHubFF: hasMarketingHubFF,
              learningCenterFF: hasActiveLearningCenterFF,
              taxDocumentsFF: hasTaxDocumentsFF,
            }}
            disableRequestToDonate={hasDisableRequestToDonate}
            isMobileView={isMobileView}
            user={user}
            isDrawerOpen={isDrawerOpen}
            closeDrawer={closeDrawer}
          />
        )}
      </nav>
    </Box>
  );
};

export default ButtonAppBar;
